import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';

// Public Pages
import HomePage from './pages/HomePage';
import NewsPage from './pages/NewsPage';
import ContactPage from './pages/ContactPage';
import SponsorsPage from './pages/SponsorsPage';
import PostDetail from './pages/PostDetail';
import CategoryPage from './pages/CategoryPage';
import SearchPage from './pages/SearchPage';
import EventsPage from './pages/EventsPage';
import SubmitEventPage from './pages/SubmitEventPage';
import EventVerifyPage from './pages/EventVerifyPage';
import FoodDrinkPage from './pages/FoodDrinkPage';
import EventDetail from './pages/EventDetail';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetail from './pages/PropertyDetail';
import RoadsAndTravel from './pages/RoadsAndTravel';
import IncidentWatchPage from './pages/IncidentWatchPage';
import JobsPage from './pages/JobsPage';
import CommunityPage from './pages/CommunityPage';
import UsefulInfoPage from './pages/UsefulInfoPage';
import StMirrenPage from './pages/StMirrenPage';
import PennyLaneProperties from './pages/PennyLaneProperties';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import { CookieNotice } from './components/CookieNotice';

// Admin Pages
import AdminLogin from './pages/AdminLogin';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminResetPassword from './pages/AdminResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import AIQueue from './pages/AIQueue';
import PostsManagement from './pages/PostsManagement';
import PostEditor from './pages/PostEditor';
import ContentImporter from './pages/ContentImporter';
import { BusinessesManager, AdvertisersManager, SupportersManager, FeaturedPartnersManager, FeaturedSectionsManager } from './pages/EntityManager';
import PartnersHub from './pages/PartnersHub';
import EventsManager from './pages/EventsManager';
import PropertiesManager from './pages/PropertiesManager';
import SubscribersManager from './pages/SubscribersManager';
import MessagesPage from './pages/MessagesPage';
import SiteSettingsPage from './pages/admin/SiteSettingsPage';
import SecurityPage from './pages/admin/SecurityPage';
import MaintenancePage from './pages/admin/MaintenancePage';
import BroadcastPage from './pages/admin/BroadcastPage';
import ClydeFlowImport from './pages/admin/ClydeFlowImport';
import MillWatchPage from './pages/admin/MillWatchPage';
import IncidentWatchAdmin from './pages/admin/IncidentWatchAdmin';
import VenueWatchAdmin from './pages/admin/VenueWatchAdmin';
import AffiliateProductsPage from './pages/admin/AffiliateProductsPage';
import EventFinderPage from './pages/admin/EventFinderPage';

// Lightweight inline redirect so /directory anywhere hops to the sister-site
// directory at paisleyscotland.com.
const DirectoryExternalRedirect = () => {
  React.useEffect(() => {
    window.location.replace('https://paisleyscotland.com/directory');
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">
      Redirecting to paisleyscotland.com/directory…
    </div>
  );
};

// WordPress legacy URL redirect component
const WPRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/news/${slug}`} replace />;
};

// Every authenticated admin page lives inside the CMS shell (sidebar + topbar).
const Admin = ({ children }) => (
  <ProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

function App() {
  useEffect(() => {
    const hide = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.style.cssText = 'display:none!important;';
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App min-h-screen bg-white">
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/food-drink" element={<FoodDrinkPage />} />
            <Route path="/news/:slug" element={<PostDetail />} />

            {/* Category Pages */}
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/submit" element={<SubmitEventPage />} />
            <Route path="/events/verify/:token" element={<EventVerifyPage />} />
            <Route path="/events/:slug" element={<EventDetail />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:slug" element={<PropertyDetail />} />
            <Route path="/roads-and-travel" element={<RoadsAndTravel />} />
            <Route path="/incident-watch" element={<IncidentWatchPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/useful-information" element={<UsefulInfoPage />} />
            <Route path="/st-mirren" element={<StMirrenPage />} />
            <Route path="/for-sale" element={<PennyLaneProperties />} />
            <Route
              path="/history"
              element={
                <CategoryPage
                  category="history"
                  path="/history"
                  title="Paisley History"
                  description="Explore the rich heritage and fascinating history of Paisley"
                />
              }
            />
            <Route
              path="/attractions"
              element={
                <CategoryPage
                  category="attractions"
                  path="/attractions"
                  title="Visitor Attractions"
                  description="Plan your visit to Paisley's must-see attractions and landmarks"
                />
              }
            />
            <Route
              path="/famous-people"
              element={
                <CategoryPage
                  category="people"
                  path="/famous-people"
                  title="Famous People"
                  description="Learn about notable figures from Paisley's past and present"
                  seoTitle="Famous People from Paisley | Notable Paisley Buddies"
                  seoDescription="Discover notable people from Paisley, Renfrewshire, including artists, actors, musicians, sports figures, inventors and community leaders."
                />
              }
            />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            <Route path="/sponsors/success" element={<SponsorsPage />} />
            <Route path="/directory" element={<DirectoryExternalRedirect />} />

            {/* Admin Routes — all wrapped in the CMS shell */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/dashboard" element={<Admin><AdminDashboard /></Admin>} />
            <Route path="/admin/posts" element={<Admin><PostsManagement /></Admin>} />
            <Route path="/admin/posts/new" element={<Admin><PostEditor /></Admin>} />
            <Route path="/admin/posts/edit/:id" element={<Admin><PostEditor /></Admin>} />
            <Route path="/admin/import" element={<Admin><ContentImporter /></Admin>} />
            <Route path="/admin/clydeflow" element={<Admin><ClydeFlowImport /></Admin>} />
            <Route path="/admin/ai-queue" element={<Admin><AIQueue /></Admin>} />
            <Route path="/admin/events" element={<Admin><EventsManager /></Admin>} />
            <Route path="/admin/events/finder" element={<Admin><EventFinderPage /></Admin>} />
            <Route path="/admin/properties" element={<Admin><PropertiesManager /></Admin>} />
            <Route path="/admin/subscribers" element={<Admin><SubscribersManager /></Admin>} />
            <Route path="/admin/broadcast" element={<Admin><BroadcastPage /></Admin>} />
            <Route path="/admin/messages" element={<Admin><MessagesPage /></Admin>} />
            <Route path="/admin/businesses" element={<Admin><BusinessesManager /></Admin>} />
            <Route path="/admin/advertisers" element={<Admin><AdvertisersManager /></Admin>} />
            <Route path="/admin/supporters" element={<Admin><SupportersManager /></Admin>} />
            <Route path="/admin/featured-partners" element={<Admin><FeaturedPartnersManager /></Admin>} />
            <Route path="/admin/partners" element={<Admin><PartnersHub /></Admin>} />
            <Route path="/admin/featured-sections" element={<Admin><FeaturedSectionsManager /></Admin>} />
            <Route path="/admin/site-settings" element={<Admin><SiteSettingsPage /></Admin>} />
            <Route path="/admin/security" element={<Admin><SecurityPage /></Admin>} />
            <Route path="/admin/mill-watch" element={<Admin><MillWatchPage /></Admin>} />
            <Route path="/admin/incident-watch" element={<Admin><IncidentWatchAdmin /></Admin>} />
            <Route path="/admin/venue-watch" element={<Admin><VenueWatchAdmin /></Admin>} />
            <Route path="/admin/affiliate" element={<Admin><AffiliateProductsPage /></Admin>} />
            <Route path="/admin/maintenance" element={<Admin><MaintenancePage /></Admin>} />

            {/* Redirect /admin to dashboard */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

            {/* WordPress legacy URL redirects */}
            <Route path="/:year/:month/:slug" element={<WPRedirect />} />

            {/* Legacy WordPress paths that were still being linked and crawled.
                Each one has real content behind it now, so send the equity to
                the live page instead of answering with the homepage. */}
            <Route path="/category/news" element={<Navigate to="/news" replace />} />
            <Route path="/paisley-events" element={<Navigate to="/events" replace />} />
            <Route path="/paisley-history" element={<Navigate to="/history" replace />} />
            <Route path="/category/visitor-attractions" element={<Navigate to="/attractions" replace />} />
            <Route path="/category/famous-people" element={<Navigate to="/famous-people" replace />} />

            {/* Anything else is genuinely gone: a real 404 page, noindexed,
                rather than a silent redirect to the homepage. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <CookieNotice />
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  </HelmetProvider>
  );
}

export default App;
